import { type ContextCollectionStatus } from '@electron/shared/types';
import { useStores } from '@littlebird/sdk';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { type ChangeEvent, createContext, useContext, useEffect } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { ContactUsButton } from '@/components/blocks/ContactUsButton';
import { Button, XStack, YStack } from '@/components/ui';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { getText } from '@/components/ui/editor';
import { Editor } from '@/components/ui/editor/Editor';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { Separator } from '@/components/ui/Separator';
import { useElectronContextCollection } from '@/features/context-collection';
import {
  JournalPaywallOverlay,
  subscriptionStore,
  useJournalLockState,
} from '@/features/subscription';
import { useHashRoute } from '@/hooks/useHashRoute';
import { cn } from '@/lib/utils';
import { AppCapability, isCapable } from '@/utils';
import { trackEvent } from '@/utils/analytics';
import { formatDate } from '@/utils/date';
import { handleExternalUrlClick } from '@/utils/platform';

import { JournalHistoryList } from './journal-history-list';
import { JournalPending } from './journal-pending';
import {
  GENERATION_STATUS_QUERY_KEY,
  generationStatusQuery,
} from './journal-queries';
import { journalApis, toJournalDisplayDate } from './journal.apis';
import { journalStore } from './journal.store';
import {
  type InsufficientContextEntry,
  isInsufficientContextEntry,
} from './journal.types';

export const JournalScreen = observer(() => {
  const { journalId } = useParams({ strict: false });
  const { journals: journalsStore } = useStores();
  const navigate = useNavigate();

  const journals = journalsStore.orderedData;
  const updateJournal = journalApis.useUpdateJournal();
  const filteredJournalsWithGaps = journalApis.useFilteredJournals();
  const { status } = useElectronContextCollection();
  const showHistory = journalStore.showHistory;

  const currentItem = journalId
    ? filteredJournalsWithGaps.find((item) => item.id === journalId)
    : null;
  const journal =
    currentItem && !isInsufficientContextEntry(currentItem)
      ? currentItem
      : null;

  const { isLocked: isJournalLocked } = useJournalLockState(journal?.date);

  const insufficientContextEntry =
    currentItem && isInsufficientContextEntry(currentItem) ? currentItem : null;

  const handleTitleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (!journal) return;

    const title = e.target.value.trim();

    if (!title.length || title === (journal.sections?.title ?? '').trim())
      return;

    updateJournal(journal.id, { title });
  };

  useEffect(() => {
    if (!journalId && filteredJournalsWithGaps.length > 0) {
      navigate({
        to: `/journal/${filteredJournalsWithGaps[0].id}`,
      });
    }
  }, [journalId, filteredJournalsWithGaps, navigate]);

  useEffect(() => {
    if (journal?.id && !journal.viewedAt) {
      journalsStore.markViewed(journal.id);
    }
  }, [journal?.id, journal?.viewedAt, journalsStore]);

  if (!journals || journals.length === 0) {
    return (
      <YStack className="size-full gap-4 px-6 pt-4 no-record">
        <FreePlanJournalBanner />
        <JournalPending />
      </YStack>
    );
  }

  return (
    <YStack className="size-full px-6 no-record">
      {showHistory && (
        <XStack className="shrink-0 justify-end py-2 md:hidden">
          <BottomSheet.Trigger sheet="journal-history" asChild>
            <Button variant="outline" size="sm" className="gap-1.5">
              <Icons.history className="size-3.5" />
              History
            </Button>
          </BottomSheet.Trigger>
        </XStack>
      )}
      <XStack className="min-h-0 flex-1 gap-4">
        <ScrollMask
          className={cn(
            'flex-1 overflow-y-auto scrollbar-hide bg-surface border border-border rounded-xl mb-4',
            'pt-8'
          )}
        >
          <YStack className="w-full self-center gap-8 mx-auto px-4">
            <FreePlanJournalBanner className="mx-6" />
            <JournalGenerationPausedBanner />
            {insufficientContextEntry && (
              <InsufficientEntryContext.Provider
                value={{ entry: insufficientContextEntry, status }}
              >
                <InsufficientContextContent />
              </InsufficientEntryContext.Provider>
            )}
            {journal?.sections?.title && (
              <div key={journal.id} className="relative">
                <YStack className="gap-2 px-6">
                  <textarea
                    key={`journal-title-${journal.id}-${journal.restoreCount}`}
                    className={cn(
                      'w-full',
                      'text-xl font-serif resize-none [field-sizing:content]',
                      'bg-transparent',
                      'focus:ring-0 focus:outline-none'
                    )}
                    onChange={handleTitleChange}
                    defaultValue={journal.sections?.title}
                    disabled={isJournalLocked}
                  />
                  <span className="text-xs text-foreground-muted">
                    {formatDate(
                      toJournalDisplayDate(journal.date),
                      'EEEE, d MMMM yyyy'
                    )}
                  </span>
                  <Separator className="mt-4" />
                </YStack>
                <div
                  className={cn(
                    'relative',
                    isJournalLocked && 'max-h-[600px] overflow-hidden'
                  )}
                >
                  <Editor
                    key={`journal-editor-${journal.id}-${journal.restoreCount}`}
                    id={`journal-editor-${journal.id}`}
                    initialContent={journal.document}
                    bottomClearance
                    onUpdate={(document) => {
                      if (isJournalLocked) return;
                      if (
                        JSON.stringify(journal.document) ===
                        JSON.stringify(document)
                      )
                        return;
                      if (!journal.document) {
                        const textContent = getText(document).trim();
                        if (!textContent) return;
                      }
                      updateJournal(journal.id, { document });
                    }}
                  />
                </div>
              </div>
            )}
          </YStack>
          {isJournalLocked && <JournalPaywallOverlay className="-mx-6" />}
        </ScrollMask>
        {showHistory && (
          <div className="hidden md:block">
            <JournalHistoryList />
          </div>
        )}
      </XStack>
      {showHistory && (
        <BottomSheet.Sheet name="journal-history" ariaLabel="Journal history">
          <BottomSheet.Header>
            <BottomSheet.Title>History</BottomSheet.Title>
          </BottomSheet.Header>
          <JournalHistoryList className="mx-auto min-h-0 max-w-lg overflow-y-auto px-4 pb-4" />
        </BottomSheet.Sheet>
      )}
    </YStack>
  );
});

const FreePlanJournalBanner = observer(
  ({ className }: { className?: string }) => {
    const { setHash } = useHashRoute();

    if (!subscriptionStore.isFree) return null;

    const handleUpgrade = () => {
      trackEvent('UPGRADE_BUTTON_CLICKED', { source: 'journal_free_banner' });
      setHash('settings/Subscription');
    };

    return (
      <XStack
        className={cn(
          'items-start justify-between gap-4 rounded-xl border border-border bg-surface px-5 py-4',
          className
        )}
      >
        <XStack className="gap-3 flex-1">
          <Icons.sparkles className="size-5 text-accent shrink-0 mt-0.5" />
          <YStack className="gap-1">
            <span className="font-serif text-foreground">
              Daily journals are a Plus feature
            </span>
            <span className="text-sm text-foreground-subtle">
              Plus members get a journal each morning, written from what you did
              the day before.
            </span>
          </YStack>
        </XStack>
        <Button size="sm" onClick={handleUpgrade} className="shrink-0">
          Upgrade
        </Button>
      </XStack>
    );
  }
);

const JournalGenerationPausedBanner = () => {
  const { journals: journalsStore } = useStores();
  const queryClient = useQueryClient();
  const { data } = useQuery(generationStatusQuery());

  if (!data?.paused) return null;

  const handleResume = async () => {
    try {
      await journalsStore.markAllViewed();
      queryClient.setQueryData(GENERATION_STATUS_QUERY_KEY, { paused: false });
      toast.success("You're all set. We'll resume generating tomorrow.");
    } catch {
      toast.error('Could not resume journal generation. Please try again.');
    }
  };

  return (
    <XStack className="items-start justify-between gap-4 rounded-xl border border-warning-6 bg-warning-2 px-5 py-4 mx-6">
      <XStack className="gap-3 flex-1">
        <Icons.pause className="size-5 text-warning-11 shrink-0 mt-0.5" />
        <YStack className="gap-1">
          <span className="font-serif text-foreground">
            Your daily journal is paused
          </span>
          <span className="text-sm text-foreground-subtle">
            The last few went unread. Resume and we'll generate a new one
            tomorrow.
          </span>
        </YStack>
      </XStack>
      <Button size="sm" onClick={handleResume} className="shrink-0">
        Resume
      </Button>
    </XStack>
  );
};

const InsufficientContextContent = () => {
  return (
    <YStack className="gap-6 px-6">
      <YStack className="gap-2">
        <span className="text-xl font-serif text-foreground">
          Insufficient Context
        </span>
        <DateText />
      </YStack>
      <Separator />
      <YStack className="gap-8 items-start">
        <YStack className="gap-4">
          <p className="text-foreground-subtle">
            Journal entries are created automatically based on your activity and
            data. During this period, there wasn't enough context or data to
            generate meaningful insights.
          </p>
          <p className="text-sm text-foreground-muted">
            This could be due to limited app usage, privacy settings, or context
            collection being off during this time.
          </p>
        </YStack>
        <YStack className="gap-2">
          <XStack className="gap-4">
            <ContextCollectionButton />
            <ContactUsButton />
          </XStack>
          <DiscordLink />
        </YStack>
      </YStack>
    </YStack>
  );
};

const DateText = () => {
  const { entry } = useContext(InsufficientEntryContext);
  const { dayCount, startDate, endDate } = entry || {
    dayCount: 0,
    startDate: '',
    endDate: '',
  };

  const formatDateRange = () => {
    if (dayCount === 1) {
      return formatDate(startDate, 'EEEE, d MMMM yyyy');
    }
    return `${formatDate(startDate, 'd MMMM yyyy')} - ${formatDate(
      endDate,
      'd MMMM yyyy'
    )}`;
  };

  return (
    <span className="text-xs text-foreground-muted">
      <span>{formatDateRange()}</span>{' '}
      {dayCount > 1 && (
        <span className="text-xs text-foreground-muted">
          ({dayCount} {dayCount === 1 ? 'day' : 'days'} missing)
        </span>
      )}
    </span>
  );
};

const ContextCollectionButton = () => {
  const canCaptureContext = isCapable(AppCapability.ContextCapture);
  const { status } = useContext(InsufficientEntryContext);
  const { resumeContextCollection } = useElectronContextCollection();

  if (canCaptureContext) {
    if (status?.state === 'running') return null;

    return (
      <Button
        size="sm"
        onClick={() => {
          resumeContextCollection().catch((error) => {
            console.error('Failed to turn on Context Awareness:', error);
            toast.error(
              'Could not turn on Context Awareness. Check accessibility permissions in Settings.'
            );
          });
        }}
      >
        Turn on Context Collection
      </Button>
    );
  }
  return (
    <Button
      size="sm"
      onClick={() => handleExternalUrlClick('https://littlebird.ai/download')}
    >
      Switch to Desktop
    </Button>
  );
};

const DiscordLink = () => {
  return (
    <span>
      <span className="text-xs text-foreground-muted">
        For real-time support, you can also join our discord.
      </span>{' '}
      <span
        className="text-xs text-accent cursor-pointer"
        onClick={() => {
          handleExternalUrlClick('https://littlebird.ai/discord');
        }}
      >
        Click here to join.
      </span>
    </span>
  );
};

const InsufficientEntryContext = createContext<{
  entry: InsufficientContextEntry | undefined;
  status: ContextCollectionStatus | undefined;
}>({
  entry: undefined,
  status: undefined,
});
